import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { UtilsService } from '../../../../shared/services/utils.service';
import { Constants } from '../../../../shared/interfaces/constants';
import { MatDividerModule } from '@angular/material/divider';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { Subject } from 'rxjs/internal/Subject';
import {
	ClipsSizeResponse,
	CreateRecordingScheduleRequest,
	ManagementMediumClippingControllerService,
	ManagementMediumResponse,
	ManagementRecordingScheduleControllerService,
	ManagementRecordingScheduleResponse,
	SaveRecordingScheduleResponse,
	UpdateRecordingScheduleRequest,
} from 'api-clipping';
import { SharedService } from '../../../../shared/services/shared.service';
import { startWith } from 'rxjs/internal/operators/startWith';
import { debounceTime } from 'rxjs/internal/operators/debounceTime';
import { distinctUntilChanged } from 'rxjs/internal/operators/distinctUntilChanged';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { take } from 'rxjs/internal/operators/take';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { UploadManagementService } from '../../../../shared/services/upload-management.service';
import { ManagementMediumContractNoticesControllerService, RelatedItemsSizeResponse } from 'api-contract-notices';
import { ConfirmationDialogComponent } from '../../../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { TimeFieldComponent } from '../../../../shared/components/time-field/time-field.component';
import { ProbeIptvResponse, RecordingService } from '../../../../shared/services/recording.service';

interface InputData {
	mode: 'NEW' | 'EDIT' | 'DELETE' | 'DELETE_REASSIGN' | 'PENDING';
	type: string;
	typeWithScope: 'medium_clipping' | 'medium_contract_notices';
	item: ManagementMediumResponse;
}

@Component({
	selector: 'app-medium-management',
	standalone: true,
	imports: [
		FormsModule,
		ReactiveFormsModule,
		TranslateModule,
		MatFormFieldModule,
		MatInputModule,
		MatIconModule,
		MatCardModule,
		MatButtonModule,
		MatTooltipModule,
		MatSelectModule,
		TimeFieldComponent,
		TranslateModule,
		MatCheckboxModule,
		MatDividerModule,
		MatAutocompleteModule,
		MatProgressSpinnerModule,
		MatDialogModule,
	],
	templateUrl: './medium.component.html',
	styleUrl: './medium.component.scss',
})
export class MediumComponent implements OnInit, OnDestroy {
	private onDestroy: Subject<any> = new Subject();
	allItems: ManagementMediumResponse[] | undefined;

	formGroup: FormGroup = new FormGroup({});
	filteredMediums: ManagementMediumResponse[] | undefined;
	mediumTypes = Constants.CLIP_TYPES.map(item => item.id);
	langOptions = this.data.typeWithScope === 'medium_clipping' ? Constants.LANGUAGES_CLIPPING_MEDIUMS : Constants.COUNTRIES;

	itemRelatedClips = 0;

	// CLIPPING-580-ish: recording schedules only exist for clipping-scoped TV/RADIO media, and
	// only once the medium itself already has an id (they're saved through their own endpoints,
	// not bundled into the medium's create/update payload) - so this list only ever loads/shows
	// in EDIT mode. NEW mode gets just the IPTV link field; schedules come after the first save.
	readonly daysOfWeek = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
	recordingSchedules: FormArray = new FormArray<FormGroup>([]);
	recordingSchedulesLoading = false;
	scheduleRowSaving: boolean[] = [];

	apiLoading = {
		management: false,
	};

	iptvProbeLoading = false;
	iptvProbeResult: ProbeIptvResponse | null = null;

	constructor(
		public dialogRef: MatDialogRef<MediumComponent>,
		@Inject(MAT_DIALOG_DATA) public data: InputData,
		private fb: FormBuilder,
		public utils: UtilsService,
		private apiClippingMedium: ManagementMediumClippingControllerService,
		private apiContractNoticesMedium: ManagementMediumContractNoticesControllerService,
		private apiRecordingSchedule: ManagementRecordingScheduleControllerService,
		private sharedService: SharedService,
		private translate: TranslateService,
		private uploadManagement: UploadManagementService,
		private dialog: MatDialog,
		private recordingService: RecordingService
	) {
		switch (this.data.mode) {
			case 'NEW':
			case 'EDIT': {
				this.formGroup = this.fb.group({
					nameCyrillic: [, [Validators.required]],
					nameLatin: [, [Validators.required]],
					type: [, [Validators.required]],
					lang: [, this.data.typeWithScope === 'medium_clipping' ? [Validators.required] : []],

					mediaUrl: [],
					siteVisits: [],
					facebookFollowers: [],
					commercialValue: [],
					facebookLink: [],
					iptvUrl: [],
					subscription: [1],
				});

				if (this.data.item) {
					this.formGroup.patchValue(this.data.item);
				}
				break;
			}
			case 'DELETE': {
				this.data.mode = 'PENDING';
				this.getRelatedClips();

				this.formGroup = this.fb.group({
					reassignTo: [],
					reassign: [false, [Validators.required]],
				});
				break;
			}
		}
	}

	ngOnInit(): void {
		if (this.formGroup.controls['reassignTo']) {
			this.formGroup.controls['reassignTo'].valueChanges.pipe(startWith(''), debounceTime(300), distinctUntilChanged(), takeUntil(this.onDestroy)).subscribe(searchTerm => {
				this.search(searchTerm);
			});
		}

		if (this.formGroup.controls['nameCyrillic']) {
			this.formGroup.controls['nameCyrillic'].valueChanges.pipe(takeUntil(this.onDestroy)).subscribe(input => {
				this.formGroup.controls['nameLatin'].setValue(input);
				this.transliterate('nameCyrillic', 'nameLatin', 'CYR_LAT');
			});
		}

		if (this.data.mode === 'EDIT' && this.data.item?.id && this.isRecordingScheduleCapable()) {
			this.loadRecordingSchedules();
		}
	}

	ngOnDestroy(): void {
		this.onDestroy.next('');
	}

	private search(searchTerm: string) {
		if (searchTerm.length >= 1) {
			this.getActiveControler()
				.searchMediums(searchTerm)
				.pipe(take(1))
				.subscribe((response: any) => {
					this.filteredMediums = response;
				});
		}
	}

	displayFn(medium: ManagementMediumResponse): string {
		return medium && medium.id ? this.utils.getParameterByLanguageScript(medium, 'name') : '';
	}

	getLangCode(lang: any): string {
		return lang.langCode || lang.countryCode;
	}

	transliterate(currentComponentName: string, targetComponentName: string, conversion: 'CYR_LAT' | 'LAT_CYR') {
		let currentComponentValue = this.formGroup.controls[currentComponentName].value;
		if (currentComponentValue) {
			this.formGroup.controls[targetComponentName].setValue(this.utils.transliterate(conversion, currentComponentValue));
		}
	}

	private getActiveControler() {
		return this.data.typeWithScope === 'medium_clipping' ? (this.apiClippingMedium as any) : (this.apiContractNoticesMedium as any);
	}

	action() {
		this.apiLoading.management = true;
		switch (this.data.mode) {
			case 'NEW': {
				this.createMedium();
				break;
			}
			case 'EDIT': {
				this.editMedium();
				break;
			}
			case 'DELETE_REASSIGN':
			case 'DELETE': {
				this.deleteMedium();
				break;
			}
		}
	}

	closeDialog() {
		this.dialogRef.close();
	}

	selectFilesForUpload(event: any) {
		this.uploadManagement.generatePresignedUrl(event.target.files[0], 'MEDIUM', 'CLIPPING');
		this.uploadManagement.linkGenerated.pipe(take(1)).subscribe(response => {
			this.formGroup.controls['mediaUrl'].setValue(response);
		});
	}

	getAcceptedFileFormats() {
		return ['image/*'];
	}

	//** MEDIUM methods */
	private createMedium() {
		this.getActiveControler()
			.createMedium(this.utils.validateRequestObject(this.formGroup.value))
			.pipe(take(1))
			.subscribe({
				next: (response: any) => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_CREATED'),
						type: 'SUCCESS',
					});

          if (this.formGroup.controls['mediaUrl'].value) {
            this.uploadManagement.uploadFile(response);
          }

					this.closeDialog();
				},
				error: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
						type: 'ERROR',
					});
					this.apiLoading.management = false;
				},
			});
	}

	private editMedium() {
		if (this.data.item && this.data.item.id) {
			this.getActiveControler()
				.updateMedium(this.utils.validateRequestObject(this.formGroup.value), this.data.item.id)
				.pipe(take(1))
				.subscribe({
					next: () => {
						this.sharedService.showNotification.next({
							message: this.translate.instant('NOTIFICATIONS.RECORD_UPDATED'),
							type: 'SUCCESS',
						});

            if (this.data.item && this.formGroup.controls['mediaUrl'].value !== this.data.item.mediaUrl) {
							this.uploadManagement.uploadFile(this.data.item);
						}

						this.closeDialog();
					},
					error: (err: any) => {
						this.sharedService.showNotification.next({
							message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${err.error.message}`,
							type: 'ERROR',
						});
						this.apiLoading.management = false;
					},
				});
		}
	}

	private deleteMedium() {
		const reassignMedium: ManagementMediumResponse = this.formGroup.controls['reassignTo'].value;
		if (this.data.item && this.data.item.id) {
			this.getActiveControler()
				.deleteMedium(this.data.item.id, reassignMedium && reassignMedium.id ? reassignMedium.id : '')
				.pipe(take(1))
				.subscribe({
					next: () => {
						this.sharedService.showNotification.next({
							message: this.translate.instant('NOTIFICATIONS.RECORD_DELETED'),
							type: 'SUCCESS',
						});
						this.closeDialog();
					},
					error: (err: any) => {
						this.sharedService.showNotification.next({
							message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${err.error.message}`,
							type: 'ERROR',
						});
						this.apiLoading.management = false;
					},
				});
		}
	}

	private getRelatedClips() {
		this.getActiveControler()
			.getMediumRelatedToSize(this.data.item.id)
			.pipe(take(1))
			.subscribe({
				next: (response: ClipsSizeResponse | RelatedItemsSizeResponse) => {
					this.itemRelatedClips = response.relatedItemsSize || 0;
					this.data.mode = this.itemRelatedClips ? 'DELETE_REASSIGN' : 'DELETE';
				},
			});
	}
	//** (END)MEDIUM methods */

	//** RECORDING SCHEDULE methods */
	isRecordingScheduleCapable(): boolean {
		const type = this.formGroup.controls['type']?.value;
		return this.data.typeWithScope === 'medium_clipping' && (type === 'TV' || type === 'RADIO');
	}

	get recordingScheduleControls(): FormGroup[] {
		return this.recordingSchedules.controls as FormGroup[];
	}

	private loadRecordingSchedules() {
		this.recordingSchedulesLoading = true;
		this.apiRecordingSchedule
			.getSchedulesForMedium(this.data.item.id)
			.pipe(take(1))
			.subscribe({
				next: (schedules: ManagementRecordingScheduleResponse[]) => {
					this.recordingSchedules.clear();
					this.scheduleRowSaving = [];
					schedules.forEach(schedule => {
						this.recordingSchedules.push(this.buildScheduleFormGroup(schedule));
						this.scheduleRowSaving.push(false);
					});
					this.recordingSchedulesLoading = false;
				},
				error: () => {
					this.recordingSchedulesLoading = false;
				},
			});
	}

	private buildScheduleFormGroup(schedule?: ManagementRecordingScheduleResponse): FormGroup {
		return this.fb.group({
			id: [schedule?.id || null],
			// The API returns/accepts LocalTime as "HH:mm[:ss]" - <input type="time"> only
			// wants "HH:mm", so seconds (if any come back) are trimmed on the way in.
			startTime: [schedule?.startTime ? schedule.startTime.substring(0, 5) : '', Validators.required],
			durationMinutes: [schedule?.durationMinutes || null, [Validators.required, Validators.min(1)]],
			daysOfWeek: [schedule?.daysOfWeek || [], Validators.required],
			enabled: [schedule ? schedule.enabled : true],
		});
	}

	addScheduleRow() {
		this.recordingSchedules.push(this.buildScheduleFormGroup());
		this.scheduleRowSaving.push(false);
	}

	removeScheduleRow(index: number) {
		const id = this.recordingScheduleControls[index].controls['id'].value;
		if (!id) {
			// Never saved - just drop it locally, nothing to delete on the server.
			this.recordingSchedules.removeAt(index);
			this.scheduleRowSaving.splice(index, 1);
			return;
		}

		this.apiRecordingSchedule
			.deleteSchedule(id)
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.recordingSchedules.removeAt(index);
					this.scheduleRowSaving.splice(index, 1);
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_DELETED'),
						type: 'SUCCESS',
					});
				},
				error: () => {
					this.sharedService.showNotification.next({
						message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
						type: 'ERROR',
					});
				},
			});
	}

	isAllDaysSelected(index: number): boolean {
		const selected = this.recordingScheduleControls[index].controls['daysOfWeek'].value || [];
		return selected.length === this.daysOfWeek.length;
	}

	toggleAllDays(index: number) {
		const control = this.recordingScheduleControls[index].controls['daysOfWeek'];
		control.setValue(this.isAllDaysSelected(index) ? [] : [...this.daysOfWeek]);
	}

	saveScheduleRow(index: number, force: boolean = false) {
		const row = this.recordingScheduleControls[index];
		if (row.invalid) {
			row.markAllAsTouched();
			return;
		}

		this.scheduleRowSaving[index] = true;

		const payload: CreateRecordingScheduleRequest | UpdateRecordingScheduleRequest = {
			startTime: row.controls['startTime'].value,
			durationMinutes: row.controls['durationMinutes'].value,
			daysOfWeek: row.controls['daysOfWeek'].value,
			enabled: row.controls['enabled'].value,
			force,
		};

		const id = row.controls['id'].value;
		const request$ = id ? this.apiRecordingSchedule.updateSchedule(id, payload) : this.apiRecordingSchedule.createSchedule(this.data.item.id, payload);

		request$.pipe(take(1)).subscribe({
			next: (response: SaveRecordingScheduleResponse) => {
				this.scheduleRowSaving[index] = false;

				if (!response.schedule) {
					// Conflict, and not forced - nothing was saved server-side. Offer the choice.
					this.showRecordingScheduleConflictDialog(index, response.conflictingSchedules || []);
					return;
				}

				row.controls['id'].setValue(response.schedule.id);
				this.sharedService.showNotification.next({
					message: this.translate.instant('NOTIFICATIONS.RECORD_UPDATED'),
					type: 'SUCCESS',
				});
			},
			error: () => {
				this.scheduleRowSaving[index] = false;
				this.sharedService.showNotification.next({
					message: this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR'),
					type: 'ERROR',
				});
			},
		});
	}

	private showRecordingScheduleConflictDialog(index: number, conflicts: ManagementRecordingScheduleResponse[]) {
		const conflictLines = conflicts
			.map(conflict => `${this.formatDaysOfWeek(conflict.daysOfWeek || [])} ${(conflict.startTime || '').substring(0, 5)} (${conflict.durationMinutes} ${this.translate.instant('MEDIUM.DURATION_MINUTES_SHORT')})`)
			.join('\n');

		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			width: '500px',
			data: {
				mode: 'USER_ACTION',
				type: 'custom-fields',
				message: this.translate.instant('MEDIUM.RECORDING_SCHEDULE_CONFLICT_MESSAGE') + '\n\n' + conflictLines,
				fields: [],
			},
		});

		dialogRef
			.afterClosed()
			.pipe(take(1))
			.subscribe(confirmed => {
				if (confirmed) {
					this.saveScheduleRow(index, true);
				}
			});
	}

	private formatDaysOfWeek(days: string[]): string {
		return days.map(day => this.translate.instant('MEDIUM.DAYS.' + day)).join(', ');
	}
	// "Проба": asks the server to open the IPTV link for a few seconds (nothing is recorded) and
	// shows whether it has picture and sound. Uses the link as typed, saved or not.
	probeIptv() {
		const url = (this.formGroup.get('iptvUrl')?.value || '').trim();
		if (!url || this.iptvProbeLoading) {
			return;
		}
		this.iptvProbeLoading = true;
		this.iptvProbeResult = null;
		this.recordingService
			.probe(url)
			.pipe(take(1))
			.subscribe({
				next: result => {
					this.iptvProbeResult = result;
					this.iptvProbeLoading = false;
				},
				error: () => {
					this.iptvProbeResult = { ok: false, hasVideo: false, hasAudio: false, message: this.translate.instant('MEDIUM.IPTV_PROBE_ERROR') };
					this.iptvProbeLoading = false;
				},
			});
	}
	//** (END)RECORDING SCHEDULE methods */
}
